import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { apiGet, apiSend } from "@/lib/api-client";
import { logger } from "@/lib/logger";
import { userDisplayKeys } from "@/lib/query";

const UserDisplaySchema = z.object({
  userId: z.string(),
  userName: z.string(),
  policyNumber: z.string().nullable(),
  policyholderName: z.string().nullable(),
  insuredId: z.string().nullable(),
  agencyNumber: z.string().nullable(),
  agencyName: z.string().nullable(),
  claimNumber: z.string().nullable(),
  searchText: z.string().nullable(),
  searchTypeId: z.string().nullable(),
  claimDetailDollarThreshold: z.number().nullable(),
  appearanceAccent: z.string().nullable(),
  appearanceBackground: z.string().nullable(),
});

const NullableUserDisplaySchema = UserDisplaySchema.nullable();

export type UserDisplay = z.infer<typeof UserDisplaySchema>;

export type UserDisplayPatch = Partial<
  Pick<
    UserDisplay,
    | "searchTypeId"
    | "searchText"
    | "policyNumber"
    | "policyholderName"
    | "insuredId"
    | "agencyNumber"
    | "agencyName"
    | "claimNumber"
    | "appearanceAccent"
    | "appearanceBackground"
  >
>;

export function useUserDisplay() {
  return useQuery({
    queryKey: userDisplayKeys.current,
    queryFn: () => apiGet("/api/user-display", NullableUserDisplaySchema),
  });
}

// Background sync mutation. Used by the home page (search input persistence)
// and dashboards (writing the URL number to UserDisplay so other MEMIC apps
// know which entity is selected). Intentionally silent on success — toasts
// would fire on every dashboard mount and on every keystroke in search.
export function useUpdateUserDisplay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UserDisplayPatch) =>
      apiSend("PUT", "/api/user-display", patch, NullableUserDisplaySchema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userDisplayKeys.all });
    },
    onError: (error: Error) => {
      logger.warn("user_display_update_failed", { message: error.message });
    },
  });
}

// Fire-and-forget UserDisplay write for dashboard pages. Writes the entity
// number to UserDisplay once per change so other MEMIC apps that read
// UserDisplay (e.g. Claim Service Summary) know what the user has selected.
export function useSyncUserDisplay(patch: UserDisplayPatch) {
  const { mutate } = useUpdateUserDisplay();
  const lastSent = useRef<string>("");
  const serialized = JSON.stringify(patch);

  useEffect(() => {
    if (lastSent.current === serialized) return;
    if (Object.values(patch).every((v) => v === undefined || v === null)) return;
    lastSent.current = serialized;
    mutate(patch);
  }, [serialized, mutate, patch]);
}
